import React, { useEffect, useRef, useState } from 'react'
import { Modal, Button, Spinner, Badge } from 'react-bootstrap'
import { closeSerialPort } from '../../generic/crud'
import { MAX_SERIAL_LINES } from '../parserPresets'
import { showToast, TOAST_FAILURE } from '../../../utils/toast/Toast'

const compileParser = (code) => {
    try {
        return new Function('data', (code || '') + '\nreturn parse(data);')
    } catch (e) {
        return null
    }
}

const formatTime = (date) =>
    date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 })

// Live serial monitor. Opens an EventSource against `serialUrl` (already carries the
// group id), parses each line with the current parserFunction, and re-parses on-screen
// lines whenever the parser changes (e.g. after AI generation).
const SerialMonitorModal = ({ show, serialUrl, parserFunction, onGenerate, generating, onHide }) => {
    const [connected, setConnected] = useState(false)
    const [status, setStatus] = useState('')
    const [lines, setLines] = useState([])
    const eventSourceRef = useRef(null)
    const terminalRef = useRef(null)

    // Open / close the serial stream with the modal.
    useEffect(() => {
        if (!show || !serialUrl) return
        setLines([])
        setStatus('Connecting...')
        setConnected(false)

        const parserRef = { fn: compileParser(parserFunction) }
        const es = new EventSource(serialUrl)
        eventSourceRef.current = es

        es.addEventListener('data', (event) => {
            let parsed = null
            let parseError = null
            if (parserRef.fn) {
                try { parsed = parserRef.fn(event.data) } catch (e) { parseError = e.message }
            } else {
                parseError = 'Parser function has syntax errors'
            }
            setLines(prev => [...prev, { type: 'data', text: event.data, time: new Date(), parsed, parseError }].slice(-MAX_SERIAL_LINES))
        })
        es.addEventListener('status', (event) => {
            setStatus(event.data)
            setConnected(event.data.startsWith('Connected') || event.data.startsWith('Reconnected'))
            setLines(prev => [...prev, { type: 'status', text: event.data, time: new Date() }])
        })
        es.addEventListener('error', (event) => {
            if (event.data) {
                setStatus(event.data)
                setLines(prev => [...prev, { type: 'error', text: event.data, time: new Date() }])
            }
            setConnected(false)
        })
        es.onerror = () => { setConnected(false); setStatus('Disconnected') }

        return () => {
            es.close()
            eventSourceRef.current = null
            closeSerialPort().catch(() => {})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, serialUrl])

    // Re-parse on-screen data lines when the parser code changes.
    useEffect(() => {
        const fn = compileParser(parserFunction)
        setLines(prev => prev.map(line => {
            if (line.type !== 'data') return line
            if (!fn) return { ...line, parsed: null, parseError: 'Parser function has syntax errors' }
            try { return { ...line, parsed: fn(line.text), parseError: null } }
            catch (e) { return { ...line, parsed: null, parseError: e.message } }
        }))
    }, [parserFunction])

    // Auto-scroll.
    useEffect(() => {
        if (terminalRef.current) terminalRef.current.scrollTop = terminalRef.current.scrollHeight
    }, [lines])

    const handleClose = async () => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close()
            eventSourceRef.current = null
        }
        try { await closeSerialPort() } catch (e) { /* ignore */ }
        setConnected(false)
        setStatus('Disconnected')
        onHide()
    }

    const handleGenerateFromLive = () => {
        const samples = lines.filter(l => l.type === 'data').map(l => l.text).slice(-40)
        if (samples.length === 0) {
            showToast('No serial data captured yet — wait for some lines first', TOAST_FAILURE)
            return
        }
        onGenerate(samples)
    }

    const dataCount = lines.filter(l => l.type === 'data').length

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static" keyboard={false}>
            <Modal.Header className="py-3 px-4 border-bottom bg-dark text-white">
                <Modal.Title className="fs-6 d-flex align-items-center">
                    <i className="bi bi-terminal me-2"></i>
                    Serial Monitor
                    <Badge bg={connected ? 'success' : 'secondary'} className="ms-3">
                        {connected ? 'Connected' : 'Disconnected'}
                    </Badge>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">
                <div className="bg-dark bg-opacity-75 px-4 py-2 border-bottom border-secondary d-flex align-items-center justify-content-between">
                    <span className="small text-light font-monospace">{status}</span>
                    <span className="small text-secondary">{dataCount} lines</span>
                </div>
                <div className="bg-dark px-4 py-1 border-bottom border-secondary d-flex" style={{ fontSize: '11px' }}>
                    <span className="text-secondary" style={{ minWidth: '95px' }}>TIME</span>
                    <span className="text-secondary flex-fill">RAW DATA</span>
                    <span className="text-secondary" style={{ minWidth: '200px' }}>PARSED OUTPUT</span>
                </div>
                <div ref={terminalRef} className="bg-dark px-4 py-3 font-monospace"
                    style={{ height: '400px', overflowY: 'auto', fontSize: '13px', lineHeight: '1.6' }}>
                    {lines.length === 0 && <span className="text-secondary">Waiting for data...</span>}
                    {lines.map((line, idx) => (
                        <div key={idx} className="d-flex">
                            <span className="text-secondary me-3 flex-shrink-0" style={{ minWidth: '95px' }}>{formatTime(line.time)}</span>
                            {line.type === 'data' && (
                                <>
                                    <span className="text-success flex-fill">{line.text}</span>
                                    <span className="flex-shrink-0" style={{ minWidth: '200px' }}>
                                        {line.parsed != null ? (
                                            <span className="text-warning"><i className="bi bi-arrow-right-short me-1"></i>{line.parsed}</span>
                                        ) : line.parseError ? (
                                            <span className="text-danger small"><i className="bi bi-x-circle me-1"></i>{line.parseError}</span>
                                        ) : (
                                            <span className="text-secondary fst-italic small">no match</span>
                                        )}
                                    </span>
                                </>
                            )}
                            {line.type === 'status' && <span className="text-info fst-italic">{line.text}</span>}
                            {line.type === 'error' && <span className="text-danger">{line.text}</span>}
                        </div>
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer className="py-2 px-4 bg-dark border-top border-secondary d-flex justify-content-between">
                <div className="d-flex align-items-center">
                    <Button variant="outline-info" size="sm" onClick={handleGenerateFromLive} disabled={generating || dataCount === 0}>
                        {generating ? (
                            <><Spinner size="sm" animation="border" className="me-1" />Generating...</>
                        ) : (
                            <><i className="bi bi-magic me-1"></i>Code using AI</>
                        )}
                    </Button>
                    <span className="text-secondary small ms-3 d-none d-md-inline">
                        Stuck on "no match"? Let AI write a parser from the data above.
                    </span>
                </div>
                <Button variant="danger" size="sm" onClick={handleClose}>
                    <i className="bi bi-stop-fill me-1"></i>Close Connection
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default SerialMonitorModal
